import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { Bed, Bath, Maximize } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

type TileColor = "correct" | "near" | "gray";

export interface GuessAttemptRow {
    digits: number[];
    colors: TileColor[];
}

export interface PuzzleActivityCardProps {
    image: string;
    address: string;
    locationLabel: string; // e.g. "West Town, IL"
    beds?: number;
    baths?: number;
    sqft?: number;
    datePlayedLabel: string; // e.g. "Played 2 days ago"
    puzzleHref: string; // link to puzzle
    guessAttempts: GuessAttemptRow[]; // up to 6
    viewerHasPlayed: boolean;
    otherUserNickname?: string; // only for other-user messaging
    profileViewed: "own" | "other";
}

const MAX_ROWS = 6;
const COLS = 8;

function tileClasses(color: TileColor) {
    switch (color) {
        case "correct":
            return "tw-bg-[#6C26C4] tw-text-white";
        case "near":
            return "tw-bg-[#F69929] tw-text-white";
        default:
            return "tw-bg-[#D1D5DB] tw-text-[#111827]";
    }
}

function MiniScoreGridIcon() {
    // Matches the "3x3 colored grid" cue from agent activity toggle.
    // Row-major so it visually reads the same.
    const colors: TileColor[] = ["correct", "near", "gray", "correct", "gray", "near", "near", "correct", "gray"];
    return (
        <div className="tw-flex tw-gap-0.5">
            {Array.from({ length: 3 }).map((_, colIndex) => {
                const col = [colors[colIndex], colors[colIndex + 3], colors[colIndex + 6]];
                return (
                    <div key={colIndex} className="tw-flex tw-flex-col tw-gap-0.5">
                        {col.map((c, rowIndex) => (
                            <div
                                key={rowIndex}
                                className={clsx(
                                    "tw-w-1.5 tw-h-1.5 sm:tw-w-2 sm:tw-h-2 tw-rounded-sm",
                                    c === "correct" && "tw-bg-[#6C26C4]",
                                    c === "near" && "tw-bg-[#F69929]",
                                    c === "gray" && "tw-bg-[#D1D5DB]",
                                )}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function ImageToggleIcon() {
    // Matches the "image" icon used in Agent activity toggle.
    return (
        <div className="tw-w-[18px] tw-h-[18px] tw-border-[1.5px] tw-border-gray-600 tw-rounded-sm tw-relative">
            <div className="tw-absolute tw-top-0.5 tw-right-0.5 tw-w-1 tw-h-1 tw-bg-[#f69929] tw-rounded-full" />
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-h-[7px] tw-bg-gray-600 tw-rounded-b-sm" />
        </div>
    );
}

export function PuzzleActivityCard(props: PuzzleActivityCardProps) {
    const {
        image,
        address,
        locationLabel,
        beds,
        baths,
        sqft,
        datePlayedLabel,
        puzzleHref,
        guessAttempts,
        viewerHasPlayed,
        otherUserNickname,
        profileViewed,
    } = props;

    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();

    const [flipped, setFlipped] = useState(false);

    const attemptsCount = guessAttempts.length;

    const paddedRows = useMemo(() => {
        const rows = guessAttempts.slice(0, MAX_ROWS).map((r) => ({
            digits: r.digits.slice(0, COLS),
            colors: r.colors.slice(0, COLS),
        }));
        while (rows.length < MAX_ROWS) {
            rows.push({
                digits: Array.from({ length: COLS }, () => 0),
                colors: Array.from({ length: COLS }, () => "gray" as const),
            });
        }
        return rows;
    }, [guessAttempts]);

    const showOtherUserGate = !viewerHasPlayed && !!otherUserNickname;

    return (
        <div
            className={clsx(
                "tw-group tw-relative tw-rounded-2xl tw-overflow-hidden tw-bg-black",
                "tw-transition-transform tw-duration-200 md:hover:tw-scale-[1.02]",
                "tw-shadow-md md:hover:tw-shadow-xl md:hover:tw-shadow-black/30",
            )}
        >
            <div className="tw-relative tw-w-full tw-aspect-[16/12] tw-overflow-hidden">
                {/* Toggle button (top-right) */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const nextFlipped = !flipped;
                        const toggleTo = nextFlipped ? "score" : "back_to_listing";
                        let puzzleUrl: string | null = null;
                        try {
                            puzzleUrl = new URL(puzzleHref, window.location.origin).href;
                        } catch {
                            puzzleUrl = null;
                        }
                        sendMixpanelEvent("Profile Card Flip", {
                            isAuthenticated,
                            pageUrl: window.location.href,
                            email: userState?.emailAddress ?? null,
                            nickname: userState?.nickname ?? null,
                            profileViewed,
                            puzzleUrl,
                            toggleTo,
                        });
                        setFlipped(nextFlipped);
                    }}
                    className={clsx(
                        "tw-absolute tw-top-2.5 tw-right-2.5 md:tw-top-3 md:tw-right-3 tw-bg-white/95 tw-backdrop-blur-md",
                        "tw-w-[44px] tw-h-[44px] tw-p-0 tw-rounded-xl hover:tw-bg-white tw-transition-all md:hover:tw-shadow-[0_4px_16px_rgba(124,58,237,0.4)] md:hover:tw-scale-105",
                        "tw-shadow-[0_4px_12px_rgba(124,58,237,0.3)]",
                        "tw-z-[2]",
                    )}
                    aria-label={flipped ? "Show photo" : "Show score"}
                >
                    <div className="tw-relative tw-flex tw-items-center tw-justify-center w-full h-full">
                        {/* Only the front (not flipped) state shows the guess-attempt badge */}
                        {!flipped && (
                            <div
                                className={clsx(
                                    "tw-absolute tw-top-[-15px] tw-right-[-6px]",
                                    "tw-bg-gradient-to-br tw-from-[#7c3aed] tw-to-[#6d28d9]",
                                    "tw-text-white tw-font-bold tw-rounded-full",
                                    "tw-flex tw-items-center tw-justify-center tw-shadow-md",
                                    "tw-min-w-5 tw-h-5",
                                    {
                                        "tw-w-5 tw-text-xs": attemptsCount < 10,
                                        "tw-w-6 tw-text-[10px]": attemptsCount >= 10 && attemptsCount < 100,
                                        "tw-px-1 tw-text-[9px]": attemptsCount >= 100,
                                    },
                                )}
                            >
                                {attemptsCount > 999 ? "999+" : attemptsCount}
                            </div>
                        )}
                        {flipped ? <ImageToggleIcon /> : <MiniScoreGridIcon />}
                    </div>
                </button>

                {/* 3D flip container */}
                <div className="tw-absolute tw-inset-0" style={{ perspective: 1000 }}>
                    <motion.div
                        animate={{ rotateY: flipped ? 180 : 0 }}
                        initial={false}
                        transition={{ duration: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
                        style={{ transformStyle: "preserve-3d" }}
                        className="tw-relative tw-w-full tw-h-full"
                    >
                        {/* Front face */}
                        <div
                            className="tw-absolute tw-inset-0"
                            style={{
                                backfaceVisibility: "hidden",
                                pointerEvents: flipped ? "none" : "auto",
                            }}
                        >
                            <Link to={puzzleHref} className="tw-block tw-w-full tw-h-full">
                                <img
                                    src={image}
                                    alt={address}
                                    className="tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-300 md:group-hover:tw-scale-[1.05]"
                                />
                <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-p-3 sm:tw-p-4 tw-pr-14 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/60 tw-to-transparent">
                                    <div className="tw-font-['Merriweather',serif] tw-text-white tw-text-sm sm:tw-text-base tw-leading-tight tw-truncate">
                                        {address}
                                    </div>
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1 tw-mt-1 tw-w-full">
                                        <span className="tw-text-white/90 tw-text-xs sm:tw-text-sm tw-min-w-0">
                                            {locationLabel}
                                        </span>
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-white/90 tw-text-xs sm:tw-text-sm tw-flex-none">
                                            {beds != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Bed className="tw-w-3.5 tw-h-3.5" />
                                                    {beds}
                                                </span>
                                            )}
                                            {baths != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Bath className="tw-w-3.5 tw-h-3.5" />
                                                    {baths}
                                                </span>
                                            )}
                                            {sqft != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Maximize className="tw-w-3.5 tw-h-3.5" />
                                                    {sqft.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="tw-text-white/70 tw-text-xs sm:tw-text-sm tw-mt-2">{datePlayedLabel}</div>
                                </div>
                            </Link>
                        </div>

                        {/* Back face */}
                        <div
                            className="tw-absolute tw-inset-0"
                            style={{
                                backfaceVisibility: "hidden",
                                transform: "rotateY(180deg)",
                                pointerEvents: flipped ? "auto" : "none",
                            }}
                        >
                            <div className="tw-w-full tw-h-full tw-bg-white tw-p-4 tw-flex tw-flex-col tw-gap-2">
                                {/* Header column */}
                                <div className="tw-flex tw-flex-col tw-gap-1 tw-pr-12">
                                    <div className="tw-font-['Merriweather',serif] tw-text-[#111827] tw-text-sm tw-truncate">
                                        {address}
                                    </div>
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1 tw-mt-1 tw-w-full">
                                        <span className="tw-text-[#6B7280] tw-text-xs tw-min-w-0">{locationLabel}</span>
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-[#6B7280] tw-text-xs tw-flex-none">
                                            {beds != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Bed className="tw-w-3.5 tw-h-3.5" />
                                                    {beds}
                                                </span>
                                            )}
                                            {baths != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Bath className="tw-w-3.5 tw-h-3.5" />
                                                    {baths}
                                                </span>
                                            )}
                                            {sqft != null && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Maximize className="tw-w-3.5 tw-h-3.5" />
                                                    {sqft.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Played sits below the address/details column */}
                                    <div className="tw-text-[#6B7280] tw-text-xs">{datePlayedLabel}</div>
                                </div>

                                {/* Score grid (extra right padding so toggle won't cover tiles) */}
                                <div className="tw-grid tw-grid-rows-6 tw-gap-1 tw-flex-1 tw-pt-1 tw-pl-4 tw-pr-4">
                                    {paddedRows.map((row, rIdx) => (
                                        <div key={rIdx} className="tw-grid tw-grid-cols-8 tw-gap-1">
                                            {Array.from({ length: COLS }).map((_, cIdx) => {
                                                const digit = row.digits[cIdx] ?? 0;
                                                const color = row.colors[cIdx] ?? "gray";
                                                const isEmptyRow = rIdx >= attemptsCount;
                                                return (
                                                    <div
                                                        key={cIdx}
                                                        className={clsx(
                                                            "tw-rounded-md tw-flex tw-items-center tw-justify-center tw-text-[12px] tw-font-semibold tw-h-6",
                                                            tileClasses(color),
                                                        )}
                                                    >
                                                        <span
                                                            className={clsx(
                                                                !viewerHasPlayed && "tw-blur-[2.5px]",
                                                                color === "gray" && "tw-text-[#111827]",
                                                            )}
                                                        >
                                                            {isEmptyRow ? "" : digit}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>

                                {showOtherUserGate && (
                                    <div className="tw-text-xs tw-text-[#374151] tw-text-center tw-pb-1 tw-w-full">
                                        <Link to={puzzleHref} className="tw-text-[#6C26C4] hover:tw-underline tw-font-semibold">
                                            Complete this puzzle
                                        </Link>{" "}
                                        to see {otherUserNickname}&rsquo;s guesses.
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

